import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import {
  handleResendConfirmationCode,
  handleConfirmAccount,
} from '../../common/auth/AuthService';
import {
  Stack,
  TextField,
  Typography,
  Button,
  Box,
  CircularProgress,
  Paper,
} from '@mui/material';
import { styled } from '@mui/system';
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from '../../common/snackbar/SnackbarActions';
import SaveIcon from '@mui/icons-material/Save';
import RepeatIcon from '@mui/icons-material/Repeat';

const Item = styled(Paper)(({ theme }) => ({
  boxShadow: 'none',
  borderRadius: 0,
  margin: theme.spacing(0.5),
}));

export default function ConfirmAccountForm(props) {
  const [resendingCode, setResendingCode] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { email, password } = props;

  const validationSchema = Yup.object().shape({
    email: Yup.string().email('Invalid Email').required('Required'),
    authenticationCode: Yup.string().required('Required'),
  });

  const handleReSendCodeClick = (e) => {
    e.preventDefault();
    setResendingCode(true);
    handleResendConfirmationCode(email)
      .then(() => {
        showSuccessSnackbar('Verification code was re-sent');
      })
      .catch((err) => {
        console.error(err);
        showErrorSnackbar(
          'There was a problem re-sending the verification code'
        );
      })
      .finally(() => {
        setResendingCode(false);
      });
  };

  return (
    <>
      <Typography style={{ textAlign: 'left' }}>
        Please check your email account for a validation code and enter it
        below. If you didn't recieve a code, please check your spam, verify that
        the email address you entered is correct, or try resending the code with
        the re-send verification code button.
      </Typography>
      <Formik
        initialValues={{
          email: email,
          authenticationCode: '',
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          handleConfirmAccount(values, password)
            .then(() => {
              setSubmitting(false);
              props.successAction();
            })
            .catch((err) => {
              console.error(err);
              setErrorMessage(err.message);
              setSubmitting(false);
            });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <>
            <Form>
              <Stack alignItems='center' justifyContent='center'>
                <Item>
                  <TextField
                    name='email'
                    type='email'
                    label='Email'
                    value={values.email}
                    placeholder='Email'
                    variant='standard'
                    error={errors.email && touched.email}
                    helperText={touched.email && errors.email}
                    sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                    disabled={true}
                    inputProps={{ maxLength: 256 }}
                  />
                </Item>
                <Item>
                  <TextField
                    name='authenticationCode'
                    type='text'
                    label='Verification Code'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.authenticationCode}
                    placeholder='Verification Code'
                    variant='standard'
                    error={
                      errors.authenticationCode && touched.authenticationCode
                    }
                    helperText={
                      touched.authenticationCode && errors.authenticationCode
                    }
                    sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                    inputProps={{ maxLength: 10 }}
                  />
                </Item>
                <Item>
                  {errorMessage && (
                    <Typography sx={{ color: 'error.main' }}>
                      {errorMessage}
                    </Typography>
                  )}
                </Item>
                <Item>
                  <Box sx={{ position: 'relative' }}>
                    <Button
                      type={'submit'}
                      disabled={isSubmitting || resendingCode}
                      variant='contained'
                    >
                      <SaveIcon style={{ marginRight: '5' }} />
                      Submit
                    </Button>
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: 'secondary.main',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-9px',
                          marginLeft: '-12px',
                        }}
                      />
                    )}
                  </Box>
                </Item>
              </Stack>
            </Form>
            <Box sx={{ position: 'relative' }}>
              <Button
                type={'submit'}
                disabled={resendingCode || isSubmitting}
                variant='contained'
                color='tertiary'
                onClick={handleReSendCodeClick}
              >
                <RepeatIcon style={{ marginRight: '5' }} />
                Re-send Code
              </Button>
              {resendingCode && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: 'secondary.main',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                  }}
                />
              )}
            </Box>
          </>
        )}
      </Formik>
    </>
  );
}
